import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api/admin';

const adminApi = axios.create({ baseURL: API });
adminApi.interceptors.request.use(cfg => {
  // sessionStorage: cleared when tab closes — more secure than localStorage for admin tokens
  const t = sessionStorage.getItem('admin_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// ── Helpers ──────────────────────────────────────────────
const ROLE_COLORS = { super_admin: '#e74c3c', support: '#3498db', readonly: '#95a5a6' };
const ROLE_LABELS = { super_admin: 'Super Admin', support: 'Support', readonly: 'Read Only' };
const STATUS_COLORS = { open: '#e67e22', in_progress: '#3498db', resolved: '#27ae60' };
const PLAN_COLORS = { free: '#95a5a6', pro: '#8b5cf6', family: '#f59e0b' };

function Badge({ children, color }) {
  return <span style={{ background: color + '20', color, border: `1px solid ${color}40`, borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>{children}</span>;
}

function Card({ children, style }) {
  return <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e8e8e8', padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', ...style }}>{children}</div>;
}

function Modal({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: '100%', maxWidth: 540, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#999' }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Input({ label, ...props }) {
  return (
    <div style={{ marginBottom: 14 }}>
      {label && <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 5 }}>{label}</label>}
      <input style={{ width: '100%', border: '1.5px solid #e0e0e0', borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none', boxSizing: 'border-box', background: '#fafafa' }} {...props} />
    </div>
  );
}

function Btn({ children, onClick, color = '#2c6b3f', disabled, style, variant = 'solid' }) {
  const base = { border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 14, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1, transition: 'all 0.15s', ...style };
  const styles = variant === 'solid' ? { ...base, background: color, color: '#fff' } : { ...base, background: 'transparent', color, border: `1.5px solid ${color}` };
  return <button onClick={onClick} disabled={disabled} style={styles}>{children}</button>;
}

// ── Sidebar ──────────────────────────────────────────────
const TABS = [
  { id: 'dashboard', label: 'Dashboard', icon: '▦' },
  { id: 'users', label: 'Nutzer', icon: '👥' },
  { id: 'events', label: 'Events', icon: '📣' },
  { id: 'email', label: 'E-Mails', icon: '✉️' },
  { id: 'support', label: 'Support', icon: '🎫' },
  { id: 'admins', label: 'Admins', icon: '🛡️' },
];

function Sidebar({ tab, setTab, admin, onLogout }) {
  return (
    <div style={{ width: 220, background: '#0f172a', minHeight: '100vh', display: 'flex', flexDirection: 'column', padding: '0', flexShrink: 0 }}>
      <div style={{ padding: '24px 20px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <div style={{ color: '#fff', fontSize: 18, fontWeight: 800, letterSpacing: 0.5 }}>Nova Admin</div>
        <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 4 }}>{admin?.email}</div>
        <Badge color={ROLE_COLORS[admin?.role] || '#888'}>{ROLE_LABELS[admin?.role] || admin?.role}</Badge>
      </div>
      <nav style={{ flex: 1, padding: '12px 10px' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, border: 'none', cursor: 'pointer', marginBottom: 2, background: tab === t.id ? 'rgba(44,107,63,0.35)' : 'transparent', color: tab === t.id ? '#4ade80' : '#94a3b8', fontSize: 14, fontWeight: tab === t.id ? 700 : 400, transition: 'all 0.15s' }}>
            <span>{t.icon}</span>{t.label}
          </button>
        ))}
      </nav>
      <div style={{ padding: 16, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        <a href="/" target="_blank" style={{ color: '#94a3b8', fontSize: 12, textDecoration: 'none', display: 'block', marginBottom: 8 }}>← Zur App</a>
        <button onClick={onLogout} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 13 }}>Abmelden</button>
      </div>
    </div>
  );
}

// ── Dashboard ────────────────────────────────────────────
function Dashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => { adminApi.get('/stats').then(r => setStats(r.data)).catch(e => console.error('Fetch stats:', e)); }, []);

  const cards = stats ? [
    { label: 'Nutzer gesamt', value: stats.total_users, color: '#3b82f6' },
    { label: 'Pro Nutzer', value: stats.pro_users, color: '#8b5cf6' },
    { label: 'Sessions heute', value: stats.sessions_today, color: '#10b981' },
    { label: 'Offene Tickets', value: stats.open_tickets, color: '#f59e0b' },
    { label: 'Ø Streak', value: stats.avg_streak?.toFixed(1), color: '#ef4444' },
    { label: 'Neue User (7d)', value: stats.new_users_7d, color: '#06b6d4' },
  ] : [];

  return (
    <div>
      <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 24 }}>Dashboard</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 14, marginBottom: 28 }}>
        {cards.map(c => (
          <Card key={c.label}>
            <div style={{ fontSize: 28, fontWeight: 800, color: c.color }}>{c.value ?? '—'}</div>
            <div style={{ fontSize: 13, color: '#888', marginTop: 4 }}>{c.label}</div>
          </Card>
        ))}
      </div>
      {stats?.platform_breakdown && (
        <Card>
          <h3 style={{ margin: '0 0 16px', fontSize: 15 }}>Plattform-Übersicht</h3>
          {Object.entries(stats.platform_breakdown).map(([k, v]) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
              <span style={{ fontWeight: 600 }}>{k}</span><span>{v}</span>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}

// ── Users ────────────────────────────────────────────────
function Users() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [resetPwd, setResetPwd] = useState('');
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    try { const r = await adminApi.get('/users', { params: { search, limit: 50 } }); setUsers(r.data.users || []); } catch (e) { console.error('Fetch users:', e); } finally { setLoading(false); }
  }, [search]);

  useEffect(() => { fetch(); }, [fetch]);

  const toggleUser = async (id, active) => {
    await adminApi.put(`/users/${id}`, { active: !active }); fetch();
  };
  const changePlan = async (id, plan) => {
    await adminApi.put(`/users/${id}`, { current_plan: plan }); fetch();
  };
  const doResetPwd = async (id) => {
    if (!resetPwd || resetPwd.length < 6) { setMsg('Mindestens 6 Zeichen'); return; }
    await adminApi.post(`/users/${id}/reset-password`, { new_password: resetPwd });
    setMsg('Passwort zurückgesetzt!'); setResetPwd('');
  };

  return (
    <div>
      <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 16 }}>Nutzerverwaltung</h2>
      <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Suchen (Name, E-Mail)..." style={{ border: '1.5px solid #e0e0e0', borderRadius: 8, padding: '10px 14px', fontSize: 14, width: 280, marginBottom: 16, outline: 'none' }} />
      {loading ? <p>Lädt...</p> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead><tr style={{ background: '#f8f9fa', textAlign: 'left' }}>
              {['Name', 'E-Mail', 'Plan', 'Streak', 'Status', 'Aktionen'].map(h => <th key={h} style={{ padding: '10px 12px', fontWeight: 600, color: '#555', borderBottom: '2px solid #e8e8e8' }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {users.map(u => (
                <tr key={u.user_id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '10px 12px', fontWeight: 500 }}>{u.name}</td>
                  <td style={{ padding: '10px 12px', color: '#666' }}>{u.email}</td>
                  <td style={{ padding: '10px 12px' }}><Badge color={PLAN_COLORS[u.current_plan] || '#888'}>{u.current_plan}</Badge></td>
                  <td style={{ padding: '10px 12px' }}>🔥 {u.focus_streak || 0}</td>
                  <td style={{ padding: '10px 12px' }}><Badge color={u.active !== false ? '#10b981' : '#ef4444'}>{u.active !== false ? 'Aktiv' : 'Gesperrt'}</Badge></td>
                  <td style={{ padding: '10px 12px', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <Btn onClick={() => setSelected(u)} color="#3b82f6" style={{ padding: '5px 10px', fontSize: 12 }}>Details</Btn>
                    <Btn onClick={() => toggleUser(u.user_id, u.active !== false)} color={u.active !== false ? '#ef4444' : '#10b981'} variant="outline" style={{ padding: '5px 10px', fontSize: 12 }}>{u.active !== false ? 'Sperren' : 'Entsperren'}</Btn>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={!!selected} onClose={() => { setSelected(null); setMsg(''); }} title={`Nutzer: ${selected?.name}`}>
        {selected && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16, fontSize: 13 }}>
              {[['E-Mail', selected.email], ['Plan', selected.current_plan], ['Streak', selected.focus_streak], ['Sessions', selected.focus_sessions]].map(([k, v]) => (
                <div key={k} style={{ background: '#f8f9fa', borderRadius: 8, padding: 10 }}><div style={{ color: '#888' }}>{k}</div><div style={{ fontWeight: 700 }}>{v}</div></div>
              ))}
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#555', display: 'block', marginBottom: 6 }}>Plan ändern</label>
              <select value={selected.current_plan} onChange={e => { changePlan(selected.user_id, e.target.value); setSelected({ ...selected, current_plan: e.target.value }); }} style={{ border: '1.5px solid #e0e0e0', borderRadius: 8, padding: '8px 12px', fontSize: 14, width: '100%' }}>
                {['free', 'pro', 'family'].map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input value={resetPwd} onChange={e => setResetPwd(e.target.value)} placeholder="Neues Passwort..." type="password" style={{ flex: 1, border: '1.5px solid #e0e0e0', borderRadius: 8, padding: '9px 12px', fontSize: 14 }} />
              <Btn onClick={() => doResetPwd(selected.user_id)} color="#f59e0b">Zurücksetzen</Btn>
            </div>
            {msg && <p style={{ color: '#10b981', fontSize: 13, marginTop: 8 }}>{msg}</p>}
          </>
        )}
      </Modal>
    </div>
  );
}

// ── Events ───────────────────────────────────────────────
function Events() {
  const [events, setEvents] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: '', message: '', type: 'info', target_plan: 'all' });

  const fetch = useCallback(async () => { try { const r = await adminApi.get('/events'); setEvents(r.data); } catch (e) { console.error('Fetch events:', e); } }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const create = async () => {
    if (!form.title || !form.message) return;
    await adminApi.post('/events', form); fetch(); setShowCreate(false); setForm({ title: '', message: '', type: 'info', target_plan: 'all' });
  };
  const toggle = async (id) => { await adminApi.put(`/events/${id}/toggle`); fetch(); };
  const remove = async (id) => { if (window.confirm('Event löschen?')) { await adminApi.delete(`/events/${id}`); fetch(); } };

  const TYPE_COLORS = { info: '#3b82f6', warning: '#f59e0b', success: '#10b981', promo: '#8b5cf6' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>Events & Ankündigungen</h2>
        <Btn onClick={() => setShowCreate(true)}>+ Neues Event</Btn>
      </div>

      {events.map(e => (
        <Card key={e.event_id} style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <Badge color={TYPE_COLORS[e.type] || '#888'}>{e.type}</Badge>
                <Badge color={e.active ? '#10b981' : '#95a5a6'}>{e.active ? 'Aktiv' : 'Inaktiv'}</Badge>
                {e.target_plan !== 'all' && <Badge color={PLAN_COLORS[e.target_plan] || '#888'}>{e.target_plan} only</Badge>}
              </div>
              <div style={{ fontWeight: 700, fontSize: 15 }}>{e.title}</div>
              <div style={{ color: '#666', fontSize: 13, marginTop: 4 }}>{e.message}</div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginLeft: 16 }}>
              <Btn onClick={() => toggle(e.event_id)} color={e.active ? '#95a5a6' : '#10b981'} variant="outline" style={{ padding: '5px 10px', fontSize: 12 }}>{e.active ? 'Deaktivieren' : 'Aktivieren'}</Btn>
              <Btn onClick={() => remove(e.event_id)} color="#ef4444" variant="outline" style={{ padding: '5px 10px', fontSize: 12 }}>Löschen</Btn>
            </div>
          </div>
        </Card>
      ))}

      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Neues Event erstellen">
        <Input label="Titel" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="z.B. Weihnachts-Aktion" />
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 5 }}>Nachricht</label>
          <textarea value={form.message} onChange={e => setForm(p => ({ ...p, message: e.target.value }))} rows={3} placeholder="Event-Beschreibung..." style={{ width: '100%', border: '1.5px solid #e0e0e0', borderRadius: 8, padding: '10px 12px', fontSize: 14, boxSizing: 'border-box', resize: 'vertical' }} />
        </div>
        <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 5 }}>Typ</label>
            <select value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))} style={{ width: '100%', border: '1.5px solid #e0e0e0', borderRadius: 8, padding: '9px 12px', fontSize: 14 }}>
              {['info', 'warning', 'success', 'promo'].map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 5 }}>Zielgruppe</label>
            <select value={form.target_plan} onChange={e => setForm(p => ({ ...p, target_plan: e.target.value }))} style={{ width: '100%', border: '1.5px solid #e0e0e0', borderRadius: 8, padding: '9px 12px', fontSize: 14 }}>
              {['all', 'free', 'pro', 'family'].map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>
        <Btn onClick={create}>Event erstellen</Btn>
      </Modal>
    </div>
  );
}

// ── Email ────────────────────────────────────────────────
function EmailSender() {
  const [form, setForm] = useState({ user_id: '', subject: '', template: 'custom', message: '' });
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    adminApi.get('/users', { params: { limit: 200 } }).then(r => setUsers(r.data.users || [])).catch(() => {});
    adminApi.get('/email/logs').then(r => setLogs(r.data || [])).catch(() => {});
  }, []);

  const send = async () => {
    if (!form.user_id || !form.subject || !form.message) { setMsg('Bitte alle Felder ausfüllen'); return; }
    setSending(true);
    try {
      await adminApi.post('/email/send', form);
      setMsg('E-Mail erfolgreich gesendet!');
      setForm({ user_id: '', subject: '', template: 'custom', message: '' });
      adminApi.get('/email/logs').then(r => setLogs(r.data || []));
    } catch (e) { setMsg('Fehler beim Senden: ' + (e.response?.data?.detail || e.message)); }
    finally { setSending(false); }
  };

  return (
    <div>
      <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 20 }}>E-Mails senden</h2>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <Card>
          <h3 style={{ margin: '0 0 16px', fontSize: 15 }}>Neue E-Mail</h3>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 5 }}>Empfänger</label>
            <select value={form.user_id} onChange={e => setForm(p => ({ ...p, user_id: e.target.value }))} style={{ width: '100%', border: '1.5px solid #e0e0e0', borderRadius: 8, padding: '9px 12px', fontSize: 14 }}>
              <option value="">Nutzer auswählen...</option>
              {users.map(u => <option key={u.user_id} value={u.user_id}>{u.name} ({u.email})</option>)}
            </select>
          </div>
          <Input label="Betreff" value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))} placeholder="Betreff..." />
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 5 }}>Vorlage</label>
            <select value={form.template} onChange={e => setForm(p => ({ ...p, template: e.target.value }))} style={{ width: '100%', border: '1.5px solid #e0e0e0', borderRadius: 8, padding: '9px 12px', fontSize: 14 }}>
              <option value="custom">Eigene Nachricht</option>
              <option value="welcome">Willkommens-E-Mail</option>
              <option value="upgrade_reminder">Pro Upgrade Erinnerung</option>
              <option value="streak_reminder">Streak Erinnerung</option>
            </select>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 5 }}>Nachricht</label>
            <textarea value={form.message} onChange={e => setForm(p => ({ ...p, message: e.target.value }))} rows={5} placeholder="Nachricht..." style={{ width: '100%', border: '1.5px solid #e0e0e0', borderRadius: 8, padding: '10px 12px', fontSize: 14, boxSizing: 'border-box', resize: 'vertical' }} />
          </div>
          {msg && <p style={{ color: msg.includes('Fehler') ? '#ef4444' : '#10b981', fontSize: 13, marginBottom: 12 }}>{msg}</p>}
          <Btn onClick={send} disabled={sending}>{sending ? 'Sende...' : 'E-Mail senden'}</Btn>
        </Card>

        <Card>
          <h3 style={{ margin: '0 0 16px', fontSize: 15 }}>Verlauf</h3>
          {logs.length === 0 ? <p style={{ color: '#999', fontSize: 13 }}>Noch keine E-Mails gesendet</p> : logs.slice(0, 20).map(l => (
            <div key={l.log_id} style={{ borderBottom: '1px solid #f0f0f0', padding: '8px 0', fontSize: 13 }}>
              <div style={{ fontWeight: 600 }}>{l.subject}</div>
              <div style={{ color: '#888' }}>An: {l.to_email} · {new Date(l.sent_at).toLocaleDateString('de')}</div>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}

// ── Support ──────────────────────────────────────────────
function Support() {
  const [tickets, setTickets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState('');
  const [filter, setFilter] = useState('all');

  const fetch = useCallback(async () => { try { const r = await adminApi.get('/support'); setTickets(r.data); } catch (e) { console.error('Fetch support:', e); } }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const sendReply = async () => {
    if (!reply.trim()) return;
    await adminApi.post(`/support/${selected.ticket_id}/reply`, { reply });
    setReply(''); fetch(); setSelected(null);
  };
  const updateStatus = async (id, status) => { await adminApi.put(`/support/${id}/status`, { status }); fetch(); };

  const filtered = filter === 'all' ? tickets : tickets.filter(t => t.status === filter);

  return (
    <div>
      <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 16 }}>Support-Tickets</h2>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {['all', 'open', 'in_progress', 'resolved'].map(s => (
          <button key={s} onClick={() => setFilter(s)} style={{ border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: filter === s ? 700 : 400, background: filter === s ? '#0f172a' : '#f0f0f0', color: filter === s ? '#fff' : '#555', cursor: 'pointer' }}>
            {s === 'all' ? 'Alle' : s === 'open' ? 'Offen' : s === 'in_progress' ? 'In Bearbeitung' : 'Gelöst'}
          </button>
        ))}
      </div>
      {filtered.map(t => (
        <Card key={t.ticket_id} style={{ marginBottom: 10, cursor: 'pointer' }} onClick={() => setSelected(t)}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>{t.subject}</div>
              <div style={{ fontSize: 13, color: '#888' }}>{t.user_email} · {new Date(t.created_at).toLocaleDateString('de')}</div>
              <div style={{ fontSize: 13, color: '#555', marginTop: 6, maxWidth: 500 }}>{t.message.slice(0, 120)}...</div>
            </div>
            <Badge color={STATUS_COLORS[t.status] || '#888'}>{t.status}</Badge>
          </div>
        </Card>
      ))}

      <Modal open={!!selected} onClose={() => setSelected(null)} title={`Ticket: ${selected?.subject}`}>
        {selected && (
          <>
            <div style={{ background: '#f8f9fa', borderRadius: 10, padding: 14, marginBottom: 16 }}>
              <div style={{ fontSize: 13, color: '#888', marginBottom: 4 }}>{selected.user_name} · {selected.user_email}</div>
              <p style={{ margin: 0, fontSize: 14 }}>{selected.message}</p>
            </div>
            {selected.admin_reply && (
              <div style={{ background: '#e8f5e9', borderRadius: 10, padding: 14, marginBottom: 16 }}>
                <div style={{ fontSize: 12, color: '#2e7d32', fontWeight: 600, marginBottom: 4 }}>Admin-Antwort:</div>
                <p style={{ margin: 0, fontSize: 14 }}>{selected.admin_reply}</p>
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              {['open', 'in_progress', 'resolved'].map(s => (
                <Btn key={s} onClick={() => { updateStatus(selected.ticket_id, s); setSelected({ ...selected, status: s }); }} color={STATUS_COLORS[s]} variant={selected.status === s ? 'solid' : 'outline'} style={{ fontSize: 12, padding: '5px 10px' }}>{s === 'open' ? 'Offen' : s === 'in_progress' ? 'In Bearb.' : 'Gelöst'}</Btn>
              ))}
            </div>
            <textarea value={reply} onChange={e => setReply(e.target.value)} rows={3} placeholder="Antwort schreiben..." style={{ width: '100%', border: '1.5px solid #e0e0e0', borderRadius: 8, padding: '10px 12px', fontSize: 14, boxSizing: 'border-box', marginBottom: 10 }} />
            <Btn onClick={sendReply}>Antworten & E-Mail senden</Btn>
          </>
        )}
      </Modal>
    </div>
  );
}

// ── Admin Management ─────────────────────────────────────
function AdminManagement({ currentAdmin }) {
  const [admins, setAdmins] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'support' });
  const [msg, setMsg] = useState('');

  const fetch = useCallback(async () => { try { const r = await adminApi.get('/admins'); setAdmins(r.data); } catch (e) { console.error('Fetch admins:', e); } }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const create = async () => {
    if (!form.name || !form.email || !form.password) { setMsg('Alle Felder ausfüllen'); return; }
    try { await adminApi.post('/admins', form); fetch(); setShowCreate(false); setForm({ name: '', email: '', password: '', role: 'support' }); setMsg(''); }
    catch (e) { setMsg(e.response?.data?.detail || 'Fehler'); }
  };
  const remove = async (id) => { if (window.confirm('Admin löschen?')) { await adminApi.delete(`/admins/${id}`); fetch(); } };

  if (currentAdmin?.role !== 'super_admin') return <div style={{ color: '#888', padding: 20 }}>Nur Super Admins können Admins verwalten.</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>Admin-Verwaltung</h2>
        <Btn onClick={() => setShowCreate(true)}>+ Admin hinzufügen</Btn>
      </div>
      {admins.map(a => (
        <Card key={a.admin_id} style={{ marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: 700 }}>{a.name}</div>
              <div style={{ fontSize: 13, color: '#888' }}>{a.email}</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Badge color={ROLE_COLORS[a.role]}>{ROLE_LABELS[a.role]}</Badge>
              {a.admin_id !== currentAdmin?.admin_id && <Btn onClick={() => remove(a.admin_id)} color="#ef4444" variant="outline" style={{ fontSize: 12, padding: '4px 10px' }}>Entfernen</Btn>}
            </div>
          </div>
        </Card>
      ))}

      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Neuen Admin erstellen">
        <Input label="Name" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
        <Input label="E-Mail" type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} />
        <Input label="Passwort" type="password" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} />
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 5 }}>Rolle</label>
          <select value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))} style={{ width: '100%', border: '1.5px solid #e0e0e0', borderRadius: 8, padding: '9px 12px', fontSize: 14 }}>
            <option value="support">Support</option>
            <option value="readonly">Read Only</option>
            <option value="super_admin">Super Admin</option>
          </select>
        </div>
        {msg && <p style={{ color: '#ef4444', fontSize: 13, marginBottom: 10 }}>{msg}</p>}
        <Btn onClick={create}>Admin erstellen</Btn>
      </Modal>
    </div>
  );
}

// ── Login ────────────────────────────────────────────────
function AdminLogin({ onLogin }) {
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const r = await axios.post(process.env.REACT_APP_BACKEND_URL + '/api/admin/auth/login', form);
      sessionStorage.setItem('admin_token', r.data.token);
      onLogin(r.data.admin);
    } catch (e) { setError(e.response?.data?.detail || 'Login fehlgeschlagen'); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 20, padding: 40, width: 380 }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ fontSize: 32, fontWeight: 800, color: '#0f172a' }}>Nova</div>
          <div style={{ color: '#888', marginTop: 4 }}>Admin-Panel</div>
        </div>
        <form onSubmit={login}>
          <Input label="E-Mail" type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="admin@novassist.online" />
          <Input label="Passwort" type="password" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} placeholder="••••••••" />
          {error && <p style={{ color: '#ef4444', fontSize: 13, marginBottom: 12 }}>{error}</p>}
          <Btn onClick={login} disabled={loading} style={{ width: '100%', padding: 12, fontSize: 15, marginTop: 4 }}>{loading ? 'Anmelden...' : 'Anmelden'}</Btn>
        </form>
        <a href="/" style={{ display: 'block', textAlign: 'center', marginTop: 16, color: '#94a3b8', fontSize: 13, textDecoration: 'none' }}>← Zurück zur App</a>
      </div>
    </div>
  );
}

// ── Main Admin App ────────────────────────────────────────
export default function AdminPanel() {
  const [admin, setAdmin] = useState(null);
  const [tab, setTab] = useState('dashboard');

  useEffect(() => {
    const token = sessionStorage.getItem('admin_token');
    if (token) {
      adminApi.get('/auth/me').then(r => setAdmin(r.data)).catch(() => sessionStorage.removeItem('admin_token'));
    }
  }, []);

  const logout = () => { sessionStorage.removeItem('admin_token'); setAdmin(null); };

  if (!admin) return <AdminLogin onLogin={setAdmin} />;

  const content = {
    dashboard: <Dashboard />,
    users: <Users />,
    events: <Events />,
    email: <EmailSender />,
    support: <Support />,
    admins: <AdminManagement currentAdmin={admin} />,
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', background: '#f8f9fa' }}>
      <Sidebar tab={tab} setTab={setTab} admin={admin} onLogout={logout} />
      <main style={{ flex: 1, padding: '32px 28px', overflowY: 'auto', maxHeight: '100vh' }}>
        {content[tab]}
      </main>
    </div>
  );
}
